
import { useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { FormFields } from './FormFields';
import { useProductForm } from './useProductForm';

export interface ProductFormData {
  id?: string;
  name: string;
  description?: string;
  price?: number;
  currency?: string;
  category?: string;
  image_url?: string;
  affiliate_link: string;
  source?: 'products' | 'recommendations';
}

interface ProductFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialData?: ProductFormData;
  onSave: () => void;
}

export function ProductForm({ 
  open, 
  onOpenChange, 
  initialData, 
  onSave 
}: ProductFormProps) {
  const {
    formData,
    isSubmitting,
    handleChange,
    handleImageUploaded,
    handleSubmit
  } = useProductForm({ initialData, onSave, onOpenChange });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? 
              initialData.source === 'recommendations' ? 
                'Edit Product Recommendation' : 
                'Edit Product' 
              : 'Add New Product'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          <FormFields 
            formData={formData}
            handleChange={handleChange}
            handleImageUploaded={handleImageUploaded}
          />
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {initialData ? 'Update Product' : 'Create Product'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
